<template>
  <div
    :class="
      cn('relative w-full flex items-center py-3 px-4 bg-primary', props.class)
    "
  >
    <div class="w-full flex flex-col justify-center grow gap-3">
      <div class="flex flex-wrap gap-1.5 ltr:md:mr-7 rtl:ml-7">
        <Badge
          v-if="duration"
          :label="duration"
          v-bind="{ ...badgeVariant, color: 'blue-light' }"
        />
        <Badge
          v-if="cost"
          :label="cost"
          v-bind="{ ...badgeVariant, color: 'blue-light' }"
        />
        <Badge :label="date" v-bind="badgeVariant" />
        <Badge v-if="time" :label="time" v-bind="badgeVariant" />
        <Badge
          v-if="item.logged_for"
          :label="item.logged_for.fullname"
          v-bind="{ ...badgeVariant, type: 'modern', icon: 'avatar' }"
        >
          <Avatar
            :src="item.logged_for.avatar_url"
            :name="item.logged_for.fullname"
            :initials="item.logged_for.avatar_initials"
            size="xs"
            class="h-4 w-4"
          />
        </Badge>
      </div>
      <div v-if="hasContent" class="flex flex-col gap-1.5">
        <div v-if="item.description" class="flex flex-col px-0.5 gap-2">
          <span
            class="text-secondary text-style-base font-medium [--lines:3] multi-line-ellipsis"
          >
            {{ item.description }}
          </span>
        </div>
        <div v-if="item.documents?.length" class="flex flex-col gap-1.5">
          <File
            v-for="document in item.documents"
            :key="document.id"
            :file="document"
            color="gray"
            size="sm"
            download
          />
        </div>
      </div>
    </div>
    <IconButton
      v-if="canDelete && !maxSmall"
      variant="tertiary"
      type="gray"
      size="sm"
      class="absolute top-[5px] ltr:right-1 rtl:left-1"
      @click="$emit('delete')"
    >
      <Icon name="delete" />
    </IconButton>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { Avatar } from "@/SharedComponents/Avatars";
import { Badge } from "@/SharedComponents/Badges";
import { IconButton } from "@/SharedComponents/Buttons";
import { File } from "@/SharedComponents/Files";
import { Icon } from "@/SharedComponents/Icon";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useDuration } from "@/Composables/useDuration";
import { useMonetary } from "@/Composables/useMonetary";
import { useDate } from "@/Composables/useDate";
import { cn } from "@/Utils/cn";

const { maxSmall } = useBreakpoints();

defineEmits(["delete"]);

const props = defineProps({
  item: { type: Object, required: true },
  type: {
    type: String,
    validator: (value) => ["time", "material"].includes(value),
    required: true,
  },
  canDelete: { type: Boolean, required: false },
  class: { type: null, required: false },
});

const badgeVariant = {
  type: "light",
  size: "lg",
};

const { formatDuration } = useDuration();

const duration = computed(() => {
  if (props.type !== "time") {
    return undefined;
  }
  return formatDuration(
    (new Date(props.item.end).getTime() -
      new Date(props.item.start).getTime()) /
      1000
  );
});

const { formatCurrency } = useMonetary();

const cost = computed(() => {
  if (props.type !== "material") {
    return undefined;
  }
  return formatCurrency(props.item.cost);
});

const { formatDate, formatTime } = useDate();

const date = computed(() => {
  if (props.type === "time") {
    return formatDate(props.item.end);
  }
  if (props.type === "material") {
    return formatDate(props.item.date);
  }
  return undefined;
});

const time = computed(() => {
  if (props.type !== "time") {
    return undefined;
  }
  return `${formatTime(props.item.start, {
    timeStyle: "short",
  })} - ${formatTime(props.item.end, { timeStyle: "short" })}`;
});

const hasContent = computed(() => {
  return props.item.description || props.item.documents?.length;
});
</script>
